import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  initializeAuth,
  logout,
  refreshAccessToken,
  selectAuthToken,
} from './authSlice.js';
import { decodeToken } from './authUtils.js';

const INACTIVITY_TIMEOUT_MS = 59 * 60 * 1000;

const AuthSessionManager = ({ children }) => {
  const dispatch = useDispatch();
  const token = useSelector(selectAuthToken);
  const inactivityTimerRef = useRef(null);

  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  useEffect(() => {
    const activityEvents = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];

    if (!token) {
      return undefined;
    }

    const resetInactivityTimer = () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }

      inactivityTimerRef.current = setTimeout(() => {
        dispatch(logout());
      }, INACTIVITY_TIMEOUT_MS);
    };

    resetInactivityTimer();

    activityEvents.forEach((event) => {
      window.addEventListener(event, resetInactivityTimer);
    });

    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      activityEvents.forEach((event) => {
        window.removeEventListener(event, resetInactivityTimer);
      });
    };
  }, [dispatch, token]);

  useEffect(() => {
    if (!token) {
      return undefined;
    }

    let tokenRefreshTimerId;
    const decoded = decodeToken(token);

    if (!decoded?.exp) {
      dispatch(logout());
      return undefined;
    }

    const currentTime = Date.now();
    const expirationTime = decoded.exp * 1000;
    const expiresIn = expirationTime - currentTime;
    const refreshTimeout = expiresIn - 5 * 60 * 1000;

    if (refreshTimeout > 0) {
      tokenRefreshTimerId = setTimeout(() => {
        dispatch(refreshAccessToken());
      }, refreshTimeout);
    } else {
      dispatch(refreshAccessToken());
    }

    return () => {
      if (tokenRefreshTimerId) {
        clearTimeout(tokenRefreshTimerId);
      }
    };
  }, [dispatch, token]);

  return children;
};

export default AuthSessionManager;
